import { onboardingInputSchema, type OnboardingInput } from "@schoolmag/contracts";

export type OnboardingField = keyof OnboardingInput;

export interface OnboardingIssue {
  field: OnboardingField;
  step: 1 | 2 | 3;
  message: string;
}

const fieldSteps: Record<OnboardingField, 1 | 2 | 3> = {
  schoolName: 1,
  schoolType: 1,
  country: 1,
  academicSystem: 1,
  primaryLanguage: 1,
  organizationName: 2,
  primaryColor: 2,
  secondaryColor: 2,
  secondaryLanguage: 3,
  website: 3,
  description: 3,
  principalName: 3
};

const messages: Partial<Record<OnboardingField, string>> = {
  organizationName: "Enter a workspace name with at least 2 characters.",
  schoolName: "Enter a school name with at least 2 characters.",
  country: "Enter a country with at least 2 characters.",
  academicSystem: "Enter an academic system with at least 2 characters.",
  primaryLanguage: "Choose the school's main language.",
  primaryColor: "Use a six-digit colour such as #635BFF.",
  secondaryColor: "Use a six-digit colour such as #16A37A.",
  website: "Enter the full website address, for example https://school.edu.my."
};

const fieldsForStep = (step: 1 | 2 | 3) => Object.entries(fieldSteps)
  .filter(([, fieldStep]) => fieldStep === step)
  .map(([field]) => field);

export function firstOnboardingIssue(input: OnboardingInput, onlyStep?: 1 | 2 | 3): OnboardingIssue | null {
  const parsed = onboardingInputSchema.safeParse(input);
  if (parsed.success) return null;
  const allowed = onlyStep ? new Set(fieldsForStep(onlyStep)) : null;
  const issue = parsed.error.issues.find((candidate) => {
    const field = String(candidate.path[0] ?? "");
    return field in fieldSteps && (!allowed || allowed.has(field));
  });
  if (!issue) return null;
  const field = String(issue.path[0]) as OnboardingField;
  return { field, step: fieldSteps[field], message: messages[field] ?? issue.message };
}

export function issueFromApiDetails(details: unknown): OnboardingIssue | null {
  if (!details || typeof details !== "object" || !("fieldErrors" in details)) return null;
  const fieldErrors = (details as { fieldErrors?: unknown }).fieldErrors;
  if (!fieldErrors || typeof fieldErrors !== "object") return null;
  for (const [candidate, value] of Object.entries(fieldErrors)) {
    if (!(candidate in fieldSteps) || !Array.isArray(value) || typeof value[0] !== "string") continue;
    const field = candidate as OnboardingField;
    return { field, step: fieldSteps[field], message: messages[field] ?? value[0] };
  }
  return null;
}
